
import DragUpload from '@/components/util/DragUpload.vue';
import List from '@/components/util/List.vue';
import StatusStepper from '@/components/orders/StatusStepperComponent.vue';
import OrderPartListComponent from '@/components/orders/OrderPartListComponent.vue';
import OrderLaborListComponent from '@/components/orders/OrderLaborListComponent.vue';
import OrderAssetListComponent from '@/components/orders/OrderAssetListComponent.vue';

import TransferOrderComponent from '@/components/orders/TransferOrderComponent.vue';

import ReassignOrderComponent from '@/components/orders/ReassignOrderComponent.vue';
import CompleteOrderComponent from '@/components/orders/CompleteOrderComponent.vue';
import DncOrderComponent from '@/components/orders/DncOrderComponent.vue';
import OpenSubStatusComponent from '@/components/orders/OpenSubStatusComponent.vue';
import CompleteSubStatusComponent from '@/components/orders/CompleteSubStatusComponent.vue';

import ApproveDenyComponent from '@/components/orders/ApproveDenyComponent.vue';

import { ColumnDisplayOption } from '@/models/ColumnDisplayOption';

import { Component, Prop, Vue } from 'vue-property-decorator';
import { mapGetters } from 'vuex';

import {
  CommentKind,
  PerformoSettings,
  Property,
  ReportsApi,
  Workorder,
  WorkorderPagedCollectionApiResponse,
  WorkordersApi,
  ValidationProblemDetails,
  FieldMetadata
} from 'performo-api';

import DetailLayout from '@/components/system/DetailsLayout.vue';
import BuildingDisplayComponent from '@/components/system/BuildingDisplayComponent.vue';
import DocumentList from '@/components/documents/DocumentListView.vue';
import QuickMessagePicker from '@/components/system/QuickMessagePicker.vue';
import FoundSetNavigation from '@/components/system/FoundSetNavigation.vue';
import CaptureSignature from '@/components/system/CaptureSignature.vue';

import ExtendedDataEditor from '@/components/orders/editors/ExtendedDataEditor.vue';
import JobPriorityEditor from '@/components/orders/editors/JobPriorityEditor.vue';
import JobTypeEditor from '@/components/orders/editors/JobTypeEditor.vue';
import TradeEditor from '@/components/orders/editors/TradeEditor.vue';

import TargetDateEditor from '@/components/orders/editors/TargetDateEditor.vue';
import StartDateEditor from '@/components/orders/editors/StartDateEditor.vue';
import DescriptionEditor from '@/components/orders/editors/DescriptionEditor.vue';

import CampusEditor from '@/components/orders/editors/CampusEditor.vue';
import BuildingEditor from '@/components/orders/editors/BuildingEditor.vue';
import SuiteEditor from '@/components/orders/editors/SuiteEditor.vue';
import FloorEditor from '@/components/orders/editors/FloorEditor.vue';
import Location3Editor from '@/components/orders/editors/Location3Editor.vue';

import StartStopTimer from '@/components/orders/StartStopTimer.vue';
import PhotoCarousel from '@/components/orders/PhotoCarousel.vue';
import ProcedureItemsComponent from '@/components/orders/ProcedureItemsComponent.vue';
import WorkOrderSetComponent from '@/components/orders/WorkOrderSetComponent.vue';
import DuplicateOrderComponent from '@/components/orders/DuplicateOrderComponent.vue';

import { Location } from '../../../models/location.model';
import { handleApiError } from '@/ApiHelpers';

@Component({
  components: {
    List,
    StatusStepper,
    DragUpload,
    ProcedureItemsComponent,
    OrderLaborListComponent,
    OrderPartListComponent,
    ReassignOrderComponent,
    TransferOrderComponent,
    CompleteOrderComponent,
    DncOrderComponent,
    OpenSubStatusComponent,
    CompleteSubStatusComponent,
    OrderAssetListComponent,
    DetailLayout,
    BuildingDisplayComponent,
    QuickMessagePicker,
    FoundSetNavigation,

    DocumentList,

    ExtendedDataEditor,
    JobPriorityEditor,
    JobTypeEditor,
    TradeEditor,
    TargetDateEditor,
    StartDateEditor,
    DescriptionEditor,
    CampusEditor,
    BuildingEditor,
    SuiteEditor,
    FloorEditor,
    Location3Editor,
    StartStopTimer,
    ApproveDenyComponent,
    PhotoCarousel,
    WorkOrderSetComponent,
    CaptureSignature,
    DuplicateOrderComponent
  },
  computed: mapGetters({
    settings: 'settings',
    reportsApi: 'reportsApi',
    ordersApi: 'ordersApi',
    currentProperty: 'currentProperty',
    currentPropertyId: 'currentPropertyId'
  })
})
export default class WorkerDetail extends Vue {
  @Prop({ required: false, type: Boolean, default: false })
  masterDetailMode?: boolean;

  private reportsApi!: ReportsApi;
  private ordersApi!: WorkordersApi;

  private currentProperty!: Property;
  private currentPropertyId!: string;

  private settings!: PerformoSettings;

  private survey = { message: '', rating: 0 };
  private customFields: FieldMetadata[] = [];
  private commentText = '';
  private commentPrivateText = '';
  private printLoading = false;
  private showSpeedDial = false;
  private openLoading = false;
  private holdLoading = false;
  private dncLoading = false;
  private customFieldsLoading = true;

  @Prop({ required: false }) pagedOrders!: WorkorderPagedCollectionApiResponse;

  get isMobile(): boolean {
    return this.$vuetify.breakpoint.mobile;
  }
  get photoUploadInstructions(): string {
    return this.isMobile ? 'Upload photos' : 'Drag your photos here to upload';
  }

  get locations(): Location[] {
    return [
      {
        id: this.order.workorderId,
        title: 'Order #' + this.order.orderNumber,
        position: { lat: this.order.latitude, lng: this.order.longitude },
        description: ''
      }
    ];
  }

  get orderNotesDisplay() {
    if (!this.order.notes) return '';
    return this.order.notes.replace(/(?:\r\n|\r|\n)/g, '<br>');
  }
  get orderPrivateNotesDisplay() {
    if (!this.order.privateNotes) return '';
    return this.order.privateNotes.replace(/(?:\r\n|\r|\n)/g, '<br>');
  }
  get showSubmitSurvey() {
    return this.survey.rating !== 0;
  }

  get holdCommandText() {
    return this.order.orderStatus === 'Open' ? 'Hold' : 'Un-hold';
  }

  get order() {
    this.filterExtendedData();
    return this.orderProp;
  }

  private filterExtendedData() {
    this.customFieldsLoading = true;
    this.ordersApi.getCustomFields().then((fields) => {
      if (!this.order.extendedData) {
        this.customFieldsLoading = false;
        return;
      }
      this.customFields = fields;

      const fieldNames = fields.map((field) => field.name);
      let keysToRemove = Object.keys(this.order?.extendedData).filter(
        (key) => !fieldNames.includes(key)
      );
      for (const keyToRemove of keysToRemove) {
        delete this.order.extendedData?.[keyToRemove];
      }
      this.order.extendedData = { ...this.order.extendedData };
      this.customFieldsLoading = false;
    });
  }

  get orderLog() {
    if (this.order.log) {
      this.order.log.sort((a, b) => {
        return (b.logId || 0) - (a.logId || 0);
      });
    }

    return this.order.log;
  }

  get logDisplayOptions(): ColumnDisplayOption[] {
    return [
      {
        displayName: 'Date',
        columnName: 'logTimestampLTZ',
        formatter: (f: Date) => {
          if (f) {
            const options: Intl.DateTimeFormatOptions = {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: 'numeric',
              minute: 'numeric',
              hour12: true
            };
            return (
              new Intl.DateTimeFormat('en-US', options).format(f) +
              ' ' +
              this.settings.homeBaseTimeZoneCode
            );
          } else return '';
        },
        dontSort: true,
        width: '195px'
      },
      {
        displayName: 'User',
        columnName: 'logUser',
        dontSort: true,
        width: '200px'
      },
      {
        displayName: 'Activity',
        columnName: 'logActivity',
        dontSort: true,
        width: 'auto'
      }
    ];
  }

  get taskDisplayOptions(): ColumnDisplayOption[] {
    return [
      { displayName: 'Sequence', columnName: 'sortOrder', dontSort: true },
      { displayName: 'Task', columnName: 'taskTextCalc', dontSort: true },
      { displayName: 'Meter Name', columnName: 'meterName', dontSort: true },
      { displayName: 'Data Entry', columnName: 'dataEntry', dontSort: true },
      { displayName: 'Meter Units', columnName: 'meterUnits', dontSort: true }
    ];
  }

  private get orderStatusIsCompleteSubStatus() {
    if (this.order.orderStatus && this.settings.completeSubStatuses) {
      return this.settings.completeSubStatuses.includes(this.order.orderStatus);
    } else return false;
  }

  private get orderStatusIsOpenSubStatus() {
    if (this.order.orderStatus && this.settings.openSubStatuses) {
      return this.settings.openSubStatuses.includes(this.order.orderStatus);
    } else return false;
  }

  private addPrivateCommentIfEnter(sender: KeyboardEvent) {
    if (sender.keyCode === 13) {
      // clear
      this.addPrivateComment();
    }
  }

  private addPrivateComment() {
    this.addComment(this.commentPrivateText, 'private');
    this.commentPrivateText = '';
  }

  private addPublicComment() {
    this.addComment(this.commentText, 'public');
    this.commentText = '';
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private extendedDataChangedHandler(newExtendedData: { [key: string]: any }) {
    this.order.extendedData = newExtendedData;
  }

  private requesterMessageClickedHandler(message: string) {
    this.commentText = message;
  }

  private internalMessageClickedHandler(message: string) {
    this.commentPrivateText = message;
  }

  private async addComment(text: string, kind: string) {
    if (text && text.length > 0) {
      try {
        await this.ordersApi.addComment({
          workorderCommentCommand: {
            commentText: text,
            workorderId: this.order.workorderId,
            commentTime: new Date(),
            kind:
              kind === 'public' ? CommentKind.NUMBER_1 : CommentKind.NUMBER_2
          }
        });
        this.refresh();
      } catch (error) {
        this.editorError(error as ValidationProblemDetails);
      }
    }
  }

  private loadOrder(index: number) {
    if (this.pagedOrders.data) {
      this.$emit('load-master-detail-order', this.pagedOrders.data[index]);
    }
  }

  private updatePagination(pageNumber: number) {
    this.$emit('update-pagination', pageNumber);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private successHandler(data: Workorder) {
    if (this.refresh) {
      this.refresh();
    }
  }

  private errorHandler(error: string | Error) {
    console.log(error);
  }

  private handleScroll() {
    const scrolledPx = document.documentElement.scrollTop;
    this.showSpeedDial = scrolledPx > 75 ? true : false;
    // Push 3dot menu above footer logo at bottom of viewport
    window.onscroll = function () {
      const stickyMenu = document.querySelector('#speedDial');
      if (window.innerHeight + window.scrollY >= document.body.offsetHeight) {
        stickyMenu?.classList.add('pf-8');
      } else {
        stickyMenu?.classList.remove('pf-8');
      }
    };
  }

  private async mounted() {
    // blank
  }

  private async created() {
    window.addEventListener('scroll', this.handleScroll);
  }

  private async destroyed() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  @Prop({ required: true })
  private orderProp!: Workorder;

  @Prop({ required: true, type: Function })
  private refresh!: (response?: Workorder) => Promise<void>;

  private async editorError(error: ValidationProblemDetails | Response) {
    handleApiError(error);

    // some data may still have changed
    this.refresh();
  }

  private printBatch() {
    this.printLoading = true;
    this.reportsApi
      .queueReport({
        queueReportCommand: {
          outputType: 'PDF',
          propertyId: this.order.propertyId,
          reportName: `Work Order Report - ${this.order.orderCategory} Order # ${this.order.orderNumber}`,
          reportId: 1000,
          foundSetIds: this.order.workorderId
        }
      })
      .then(() => {
        this.printLoading = false;
        this.$router.push({ name: 'reports', params: {} });
      });
    // 1000 = detail
    // 1002 = list, details
    // 1004 = list, list
  }

  get canEdit() {
    let canDoQEdit = true;
    this.$store.getters.currentUser.profile.candoq.forEach((candoq: string) => {
      if (candoq === 'Edit Orders: Limited') {
        canDoQEdit = false;
      }
    });
    return canDoQEdit;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private successDocUploadHandler(data: Document) {
    this.refresh();
  }

  private errorDocUploadHandler(error: string | Error) {
    console.log(error);
  }

  private async onOpenClickHandler() {
    if (!this.order.workorderId) return;

    this.openLoading = true;
    try {
      await this.ordersApi.openOrder({ workorderId: this.order.workorderId });
      this.refresh();
    } catch (error) {
      this.editorError(error as ValidationProblemDetails);
    }
    this.openLoading = false;
  }

  private async onHoldClickHandler() {
    if (!this.order.workorderId) return;

    this.holdLoading = true;
    try {
      await this.ordersApi.setHold({ workorderId: this.order.workorderId });
      this.refresh();
    } catch (error) {
      this.editorError(error as ValidationProblemDetails);
    }
    this.holdLoading = false;
  }
}
